import toast from 'react-hot-toast';
import { CustomSelect } from '@/components';
import { PlusIcon, GripVertical } from './Icons';
import trashIcon from '../assets/trashIcon.png';
import {
  CARD_BODY_MAX,
  CAROUSEL_MAX_BUTTONS,
  CAROUSEL_MAX_CARDS,
  CAROUSEL_MIN_CARDS,
  getButtonFieldError,
} from '../utils/templateValidation';
import { fileToRawBase64 } from '../utils/buildCreateTemplatePayload';

export function CarouselCardsSection({
  cards,
  cardHeaderType,
  onHeaderTypeChange,
  onCardsChange,
  disabled,
}) {
  const updateCards = (updater) => {
    onCardsChange((prev) =>
      typeof updater === 'function' ? updater(prev) : updater,
    );
  };

  const handleHeaderTypeChange = (type) => {
    onHeaderTypeChange(type);
    updateCards((prev) =>
      prev.map((card) => {
        if (card.previewUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(card.previewUrl);
        }
        return {
          ...card,
          header_type: type,
          previewUrl: null,
          image_base64: '',
          video_base64: '',
        };
      }),
    );
  };

  const handleAddCard = () => {
    if (cards.length >= CAROUSEL_MAX_CARDS) {
      toast.error(`You can add a maximum of ${CAROUSEL_MAX_CARDS} cards.`);
      return;
    }
    const source = cards[0];
    const newCard = {
      id: `${Date.now()}-${Math.random()}`,
      header_type: cardHeaderType,
      previewUrl: null,
      image_base64: '',
      video_base64: '',
      body_text: '',
      buttons: (source?.buttons || []).map((btn) => ({
        id: `${Date.now()}-${Math.random()}-${btn.type}`,
        type: btn.type,
        text: '',
        value: '',
      })),
    };
    updateCards([...cards, newCard]);
  };

  const handleRemoveCard = (id) => {
    if (cards.length <= CAROUSEL_MIN_CARDS) {
      toast.error(
        `Carousel templates need at least ${CAROUSEL_MIN_CARDS} cards.`,
      );
      return;
    }
    updateCards(cards.filter((card) => card.id !== id));
  };

  const handleDuplicateCard = (id) => {
    if (cards.length >= CAROUSEL_MAX_CARDS) {
      toast.error(`You can add a maximum of ${CAROUSEL_MAX_CARDS} cards.`);
      return;
    }
    const index = cards.findIndex((card) => card.id === id);
    if (index < 0) return;
    const source = cards[index];
    const copy = {
      ...source,
      id: `${Date.now()}-${Math.random()}`,
      previewUrl: source.image_base64
        ? `data:image/jpeg;base64,${source.image_base64}`
        : source.video_base64
          ? `data:video/mp4;base64,${source.video_base64}`
          : null,
      buttons: (source.buttons || []).map((btn) => ({
        ...btn,
        id: `${Date.now()}-${Math.random()}-${btn.type}`,
      })),
    };
    const next = [...cards];
    next.splice(index + 1, 0, copy);
    updateCards(next);
  };

  const handleBodyChange = (id, value) => {
    updateCards(
      cards.map((card) =>
        card.id === id
          ? { ...card, body_text: value.slice(0, CARD_BODY_MAX) }
          : card,
      ),
    );
  };

  const handleMediaSelect = async (id, file) => {
    if (!file) return;
    try {
      const base64 = await fileToRawBase64(file);
      const previewUrl = URL.createObjectURL(file);
      updateCards((prev) =>
        prev.map((card) => {
          if (card.id !== id) return card;
          if (card.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(card.previewUrl);
          }
          return {
            ...card,
            previewUrl,
            image_base64: cardHeaderType === 'IMAGE' ? base64 : '',
            video_base64: cardHeaderType === 'VIDEO' ? base64 : '',
          };
        }),
      );
      toast.success(
        `${cardHeaderType === 'IMAGE' ? 'Image' : 'Video'} selected.`,
      );
    } catch {
      toast.error('Failed to read media file.');
    }
  };

  const syncButtonStructure = (mutator) => {
    const first = mutator(cards[0]?.buttons || []);
    updateCards(
      cards.map((card) => {
        const current = card.buttons || [];
        const nextButtons = first.map((structBtn, index) => {
          const existing = current[index];
          return {
            id: existing?.id || `${Date.now()}-${Math.random()}-${index}`,
            type: structBtn.type,
            text: existing?.text || '',
            value:
              structBtn.type === 'quick_reply' ? '' : existing?.value || '',
          };
        });
        return { ...card, buttons: nextButtons };
      }),
    );
  };

  const handleAddButton = () => {
    const count = cards[0]?.buttons?.length || 0;
    if (count >= CAROUSEL_MAX_BUTTONS) {
      toast.error(
        `You can add a maximum of ${CAROUSEL_MAX_BUTTONS} buttons per card.`,
      );
      return;
    }
    syncButtonStructure((buttons) => [
      ...buttons,
      { type: 'quick_reply', text: '', value: '' },
    ]);
  };

  const handleRemoveButton = (buttonIndex) => {
    syncButtonStructure((buttons) =>
      buttons.filter((_, i) => i !== buttonIndex),
    );
  };

  const handleButtonTypeChange = (buttonIndex, type) => {
    syncButtonStructure((buttons) =>
      buttons.map((btn, i) => (i === buttonIndex ? { ...btn, type } : btn)),
    );
  };

  const handleButtonFieldChange = (cardId, buttonIndex, field, value) => {
    updateCards(
      cards.map((card) => {
        if (card.id !== cardId) return card;
        return {
          ...card,
          buttons: card.buttons.map((btn, i) =>
            i === buttonIndex
              ? {
                  ...btn,
                  [field]: field === 'text' ? value.slice(0, 25) : value,
                }
              : btn,
          ),
        };
      }),
    );
  };

  const handleDropButton = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || Number.isNaN(fromIndex)) return;
    syncButtonStructure((buttons) => {
      const next = [...buttons];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-[10px] laptop:gap-2 animate-fadeIn">
      <h3 className="text-[18px] leading-[26px] laptop:text-[18px] laptop:leading-5 font-medium text-primary font-sf-pro-display">
        Carousel Cards
      </h3>

      <div className="bg-form-background-light rounded-[12px] p-4 laptop:p-3 flex flex-col gap-3 laptop:gap-2.5">
        <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
          <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
            Media Type
          </label>
          <div className="grid grid-cols-2 min-[460px]:grid-cols-4 gap-2">
            {['IMAGE', 'VIDEO'].map((type) => (
              <button
                key={type}
                type="button"
                disabled={disabled}
                onClick={() => handleHeaderTypeChange(type)}
                className={`h-[38px] laptop:h-9 px-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center text-center select-none ${
                  cardHeaderType === type
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white border border-surface-mint text-text-secondary hover:text-text-primary'
                }`}
              >
                {type === 'IMAGE' ? 'Image' : 'Video'}
              </button>
            ))}
          </div>
          <span className="text-xxs text-text-muted">
            All cards use the same media type. Button count, types, and order
            stay in sync.
          </span>
        </div>

        {cards.map((card, cardIndex) => (
          <div
            key={card.id}
            className="p-4 laptop:p-3 rounded-2xl bg-white border border-surface-mint flex flex-col gap-3 laptop:gap-2.5"
          >
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-semibold text-primary">
                Card {cardIndex + 1}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleDuplicateCard(card.id)}
                  className="px-2 py-1 text-xxs font-bold text-primary hover:bg-surface-mint rounded-lg"
                >
                  Duplicate
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleRemoveCard(card.id)}
                  className="p-1 hover:bg-red-500/10 rounded-lg"
                >
                  <img
                    src={trashIcon}
                    alt="Delete"
                    className="w-4 h-4 object-contain"
                  />
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
              <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                Upload {cardHeaderType === 'IMAGE' ? 'Image' : 'Video'}
              </label>
              <div className="w-full border border-dashed border-surface-mint rounded-2xl p-5 laptop:p-3 bg-white transition-all flex flex-col items-center justify-center text-center relative hover:border-primary/30 cursor-pointer">
                <input
                  type="file"
                  accept={cardHeaderType === 'IMAGE' ? 'image/*' : 'video/*'}
                  disabled={disabled}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleMediaSelect(card.id, file);
                    e.target.value = '';
                  }}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
                {card.previewUrl ? (
                  cardHeaderType === 'VIDEO' ? (
                    <video
                      src={card.previewUrl}
                      className="w-full max-h-32 laptop:max-h-24 object-cover rounded-lg mb-2 laptop:mb-1"
                      muted
                    />
                  ) : (
                    <img
                      src={card.previewUrl}
                      alt=""
                      className="w-full max-h-32 laptop:max-h-24 object-cover rounded-lg mb-2 laptop:mb-1"
                    />
                  )
                ) : (
                  <div className="w-10 h-10 laptop:w-8 laptop:h-8 rounded-full bg-form-background-light border border-surface-mint flex items-center justify-center text-text-secondary mb-2 laptop:mb-1">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <rect
                        width="18"
                        height="18"
                        x="3"
                        y="3"
                        rx="2"
                        strokeWidth="2"
                      />
                      <circle cx="9" cy="9" r="2" strokeWidth="2" />
                      <path
                        strokeWidth="2"
                        strokeLinecap="round"
                        d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
                      />
                    </svg>
                  </div>
                )}
                <span className="text-xs text-text-secondary font-bold">
                  Click to upload{' '}
                  {cardHeaderType === 'IMAGE' ? 'image' : 'video'}
                </span>
                <span className="text-xxs text-text-muted mt-1">
                  {card.previewUrl ? 'File attached' : 'Required for each card'}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 laptop:gap-[5px]">
              <label className="text-xs laptop:text-[13px] laptop:leading-[18px] font-semibold text-text-secondary">
                Card Body
              </label>
              <textarea
                value={card.body_text}
                disabled={disabled}
                onChange={(e) => handleBodyChange(card.id, e.target.value)}
                placeholder="Card text... use {{1}}, {{2}} for variables"
                rows={3}
                className="w-full p-3 laptop:p-2.5 rounded-input bg-form-background-light border-0 text-text-primary text-sm font-sf-pro-display placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-primary transition-all resize-none no-scrollbar"
              />
              <div className="flex justify-end text-xxs text-text-muted -mt-1">
                <span>
                  {card.body_text.length} / {CARD_BODY_MAX}
                </span>
              </div>
            </div>

            {(card.buttons || []).map((btn, buttonIndex) => {
              const errorMsg = getButtonFieldError(btn);
              const isUrl = btn.type === 'url';
              return (
                <div
                  key={btn.id}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', String(buttonIndex));
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIndex = parseInt(
                      e.dataTransfer.getData('text/plain'),
                      10,
                    );
                    handleDropButton(fromIndex, buttonIndex);
                  }}
                  className="p-3 laptop:p-2.5 rounded-xl bg-form-background-light flex flex-col gap-2 laptop:gap-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-text-muted" />
                      <span className="text-xs font-semibold text-primary">
                        Button {buttonIndex + 1}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveButton(buttonIndex)}
                      className="p-1 hover:bg-red-500/10 rounded-lg"
                    >
                      <img
                        src={trashIcon}
                        alt="Delete"
                        className="w-3.5 h-3.5"
                      />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <CustomSelect
                      value={btn.type}
                      onChange={(val) =>
                        handleButtonTypeChange(buttonIndex, val)
                      }
                      options={[
                        { value: 'quick_reply', label: 'Quick Reply' },
                        { value: 'url', label: 'Call to Action (URL)' },
                        { value: 'phone', label: 'Call to Action (Phone)' },
                      ]}
                      className="w-full h-[38px] laptop:h-9"
                      buttonClassName="w-full h-full flex items-center justify-between rounded-input bg-white border-0 text-text-primary text-sm font-sf-pro-display focus:outline-none transition-all px-3"
                      textSizeClass="text-sm"
                    />
                    <input
                      type="text"
                      value={btn.text}
                      onChange={(e) =>
                        handleButtonFieldChange(
                          card.id,
                          buttonIndex,
                          'text',
                          e.target.value,
                        )
                      }
                      placeholder="Button text"
                      className="w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white border-0 text-text-primary text-sm placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  {btn.type !== 'quick_reply' && (
                    <div className="flex flex-col gap-1">
                      <input
                        type="text"
                        value={btn.value}
                        onChange={(e) =>
                          handleButtonFieldChange(
                            card.id,
                            buttonIndex,
                            'value',
                            e.target.value,
                          )
                        }
                        placeholder={
                          isUrl
                            ? 'e.g. https://example.com'
                            : 'e.g. +91 9876543210'
                        }
                        className={`w-full h-[38px] laptop:h-9 px-3 rounded-input bg-white text-text-primary text-sm placeholder-text-muted focus:outline-none ${
                          errorMsg
                            ? 'border border-red-500'
                            : 'border-0 focus:ring-1 focus:ring-primary'
                        }`}
                      />
                      {errorMsg && (
                        <span className="text-xxs text-red-500 font-medium">
                          {errorMsg}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {(card.buttons?.length || 0) < CAROUSEL_MAX_BUTTONS && (
              <button
                type="button"
                onClick={handleAddButton}
                disabled={disabled}
                className="flex items-center justify-center gap-2 h-10 rounded-2xl border-2 border-dashed border-primary/40 text-primary text-sm font-semibold hover:bg-surface-mint"
              >
                <span>Add Button</span>
                <PlusIcon />
              </button>
            )}
            {cardIndex > 0 && (cards[0]?.buttons?.length || 0) > 0 && (
              <span className="text-xxs text-text-muted">
                Button types and order match Card 1. Edit labels and URLs per
                card.
              </span>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={handleAddCard}
          disabled={disabled || cards.length >= CAROUSEL_MAX_CARDS}
          className="flex items-center justify-center gap-2 h-12 laptop:h-10 rounded-2xl border-2 border-dashed border-primary/40 text-primary text-sm font-semibold hover:bg-surface-mint disabled:opacity-30 disabled:pointer-events-none"
        >
          <span>Add Card</span>
          <PlusIcon />
        </button>
      </div>
    </div>
  );
}
